import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

function arrayBufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

function base64urlToArrayBuffer(str: string): ArrayBuffer {
  const base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  const pad = base64.length % 4;
  const padded = pad ? base64 + "=".repeat(4 - pad) : base64;
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export const useWebAuthn = () => {
  const { user } = useAuth();
  const [isAvailable, setIsAvailable] = useState(false);
  const [hasCredential, setHasCredential] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkAvailability();
  }, []);

  useEffect(() => {
    if (user) {
      checkUserCredential();
    }
  }, [user]);

  const checkAvailability = async () => {
    if (
      typeof window !== "undefined" &&
      window.PublicKeyCredential &&
      typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === "function"
    ) {
      const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      setIsAvailable(available);
    }
  };

  const checkUserCredential = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("webauthn_credentials")
      .select("id")
      .eq("user_id", user.id)
      .limit(1);
    setHasCredential(!!(data && data.length > 0));
  };

  const register = async (): Promise<{ success: boolean; error?: string; credentialId?: string }> => {
    if (!user) return { success: false, error: "Not authenticated" };
    setLoading(true);
    try {
      const origin = window.location.origin;

      // Get registration options
      const { data: options, error: optErr } = await supabase.functions.invoke("webauthn", {
        body: { action: "register-options", origin },
      });

      if (optErr || !options) throw new Error(optErr?.message || "Failed to get options");

      // Create credential using native API
      const credential = (await navigator.credentials.create({
        publicKey: {
          challenge: base64urlToArrayBuffer(options.challenge),
          rp: options.rp,
          user: {
            id: base64urlToArrayBuffer(options.user.id),
            name: options.user.name,
            displayName: options.user.displayName,
          },
          pubKeyCredParams: options.pubKeyCredParams,
          timeout: options.timeout,
          attestation: options.attestation as AttestationConveyancePreference,
          excludeCredentials: (options.excludeCredentials || []).map((c: any) => ({
            id: base64urlToArrayBuffer(c.id),
            type: c.type,
          })),
          authenticatorSelection: options.authenticatorSelection,
        },
      })) as PublicKeyCredential;

      if (!credential) throw new Error("Credential creation cancelled");

      const response = credential.response as AuthenticatorAttestationResponse;

      // Get public key in SPKI format
      const publicKeyBuffer = response.getPublicKey?.();
      if (!publicKeyBuffer) throw new Error("getPublicKey not supported");

      // Send to server for verification
      const { data: verifyResult, error: verifyErr } = await supabase.functions.invoke("webauthn", {
        body: {
          action: "register-verify",
          origin,
          credential: {
            id: credential.id,
            type: credential.type,
            response: {
              clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
              attestationObject: arrayBufferToBase64url(response.attestationObject),
              publicKey: arrayBufferToBase64url(publicKeyBuffer),
              transports: response.getTransports?.() || [],
            },
          },
        },
      });

      if (verifyErr || !verifyResult?.success) {
        throw new Error(verifyResult?.error || verifyErr?.message || "Verification failed");
      }

      setHasCredential(true);
      return { success: true, credentialId: credential.id };
    } catch (error: any) {
      console.error("WebAuthn register error:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const login = async (): Promise<{ success: boolean; error?: string }> => {
    setLoading(true);
    try {
      const origin = window.location.origin;

      // Get login options
      const { data: options, error: optErr } = await supabase.functions.invoke("webauthn", {
        body: { action: "login-options", origin },
      });

      if (optErr || !options) throw new Error(optErr?.message || "Failed to get options");

      // Get credential using native API (discoverable - browser shows account picker)
      const credential = (await navigator.credentials.get({
        publicKey: {
          challenge: base64urlToArrayBuffer(options.challenge),
          rpId: options.rpId,
          timeout: options.timeout,
          userVerification: options.userVerification as UserVerificationRequirement,
          allowCredentials: [],
        },
      })) as PublicKeyCredential;

      if (!credential) throw new Error("Authentication cancelled");

      const response = credential.response as AuthenticatorAssertionResponse;

      // Send to server for verification
      const { data: verifyResult, error: verifyErr } = await supabase.functions.invoke("webauthn", {
        body: {
          action: "login-verify",
          origin,
          challengeId: options.challengeId,
          credential: {
            id: credential.id,
            type: credential.type,
            response: {
              clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
              authenticatorData: arrayBufferToBase64url(response.authenticatorData),
              signature: arrayBufferToBase64url(response.signature),
              userHandle: response.userHandle
                ? arrayBufferToBase64url(response.userHandle)
                : null,
            },
          },
        },
      });

      if (verifyErr || !verifyResult?.success) {
        throw new Error(verifyResult?.error || verifyErr?.message || "Verification failed");
      }

      // Use the magic link token to sign in
      const { error: otpError } = await supabase.auth.verifyOtp({
        token_hash: verifyResult.token_hash,
        type: "magiclink",
      });

      if (otpError) throw otpError;

      localStorage.setItem("biometric_authenticated", "true");
      localStorage.setItem("mfa_verified", JSON.stringify({ userId: user?.id || "", timestamp: Date.now() }));

      return { success: true };
    } catch (error: any) {
      console.error("WebAuthn login error:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  return { isAvailable, hasCredential, loading, register, login, checkUserCredential };
};
